import { z } from 'zod';
import { PlaceStatusSchema } from './enums';

/*
 * The pilot list ("Lista piloto"): the places chosen for the beta and their reserve,
 * each linked by hand or by the import to its FSQ OS Places record. The database
 * decides every change (public.pilot_list and friends); this module reads its answers.
 */

/** A row of the list is selected or in the reserve ('dropped' rows are not listed). */
export const PILOT_LIST_ROLES = ['selected', 'reserve'] as const;
export type PilotListRole = (typeof PILOT_LIST_ROLES)[number];

/** private.pilot_link_state(): linked, never matched, several close records, or not in FSQ. */
export const PILOT_LINK_STATES = ['linked', 'unmatched', 'doubtful', 'not_in_fsq'] as const;
export type PilotLinkState = (typeof PILOT_LINK_STATES)[number];

/** Share of the selected places that must be linked to FSQ (the import report's goal). */
export const PILOT_LINK_GOAL_RATIO = 0.9;

export function pilotGoal(selected: number): number {
  return Math.ceil(selected * PILOT_LINK_GOAL_RATIO);
}

/** One row of public.pilot_list(). */
export const PilotRowSchema = z
  .object({
    list_key: z.string().min(1),
    name: z.string().min(1),
    zone_id: z.number().int(),
    zone_name: z.string().min(1),
    role: z.enum(PILOT_LIST_ROLES),
    list_rank: z.number().int().positive(),
    place_id: z.string().nullable(),
    place_name: z.string().nullable(),
    fsq_id: z.string().nullable(),
    link_state: z.enum(PILOT_LINK_STATES),
    status: PlaceStatusSchema.nullable(),
    status_source: z.string().nullable(),
    status_at: z.string().nullable(),
  })
  .transform((row) => ({
    listKey: row.list_key,
    name: row.name,
    zoneId: row.zone_id,
    zoneName: row.zone_name,
    role: row.role,
    listRank: row.list_rank,
    placeId: row.place_id,
    placeName: row.place_name,
    fsqId: row.fsq_id,
    linkState: row.link_state,
    status: row.status,
    statusSource: row.status_source,
    statusAt: row.status_at,
  }));
export type PilotRow = z.infer<typeof PilotRowSchema>;
export const PilotListSchema = z.array(PilotRowSchema);

/** One row of public.pilot_link_candidates(): an open FSQ place of the row's zone. */
export const PilotCandidateSchema = z
  .object({
    place_id: z.uuid(),
    name: z.string().min(1),
    address: z.string().nullable(),
    fsq_id: z.string().min(1),
    similarity: z.number().min(0).max(1),
    distance_m: z.number().nonnegative().nullable(),
    /** The list row that already holds the place, if any. */
    taken_by_list_key: z.string().nullable(),
    taken_by_name: z.string().nullable(),
  })
  .transform((candidate) => ({
    placeId: candidate.place_id,
    name: candidate.name,
    address: candidate.address,
    fsqId: candidate.fsq_id,
    similarity: candidate.similarity,
    distanceM: candidate.distance_m,
    takenByListKey: candidate.taken_by_list_key,
    takenByName: candidate.taken_by_name,
  }));
export type PilotCandidate = z.infer<typeof PilotCandidateSchema>;
export const PilotCandidateListSchema = z.array(PilotCandidateSchema);

/** Below this name similarity a candidate is a long shot: shown apart, on request. */
export const PILOT_LOW_SIMILARITY = 0.3;

export function splitPilotCandidates(candidates: readonly PilotCandidate[]): {
  likely: PilotCandidate[];
  unlikely: PilotCandidate[];
} {
  return {
    likely: candidates.filter((candidate) => candidate.similarity >= PILOT_LOW_SIMILARITY),
    unlikely: candidates.filter((candidate) => candidate.similarity < PILOT_LOW_SIMILARITY),
  };
}

/** Length in characters as a person counts them (code points, so an emoji is one). */
export function characterCount(text: string): number {
  return [...text].length;
}

/** The reason an admin gives for a swap: 3 to 80 characters, as swap_pilot_place checks. */
export const PilotSwapReasonSchema = z
  .string()
  .trim()
  .refine((reason) => characterCount(reason) >= 3 && characterCount(reason) <= 80, {
    error: 'reason_required',
  });

export interface PilotProgress {
  readonly selected: number;
  readonly linked: number;
  readonly goal: number;
  readonly reserve: number;
  readonly reserveLinked: number;
}

export interface PilotSummary {
  readonly total: PilotProgress;
  readonly zones: readonly (PilotProgress & {
    readonly zoneId: number;
    readonly zoneName: string;
  })[];
}

function progressOf(rows: readonly PilotRow[]): PilotProgress {
  const selected = rows.filter((row) => row.role === 'selected');
  const reserve = rows.filter((row) => row.role === 'reserve');
  return {
    selected: selected.length,
    linked: selected.filter((row) => row.linkState === 'linked').length,
    goal: pilotGoal(selected.length),
    reserve: reserve.length,
    reserveLinked: reserve.filter((row) => row.linkState === 'linked').length,
  };
}

/** Linked selected places against the goal, for the whole list and per zone (list order). */
export function summarizePilot(rows: readonly PilotRow[]): PilotSummary {
  const zones = new Map<number, string>();
  for (const row of rows) zones.set(row.zoneId, row.zoneName);
  return {
    total: progressOf(rows),
    zones: [...zones].map(([zoneId, zoneName]) => ({
      zoneId,
      zoneName,
      ...progressOf(rows.filter((row) => row.zoneId === zoneId)),
    })),
  };
}

export const PILOT_FILTERS = ['all', 'unlinked', 'doubtful', 'linked', 'reserve'] as const;
export type PilotFilter = (typeof PILOT_FILTERS)[number];

/**
 * "Sin vincular" also holds the rows marked as not in FSQ: they are unlinked and wait
 * for a replacement. The link filters cover both roles; "Reserva" shows the reserve.
 */
export function matchesPilotFilter(row: PilotRow, filter: PilotFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'unlinked':
      return row.linkState === 'unmatched' || row.linkState === 'not_in_fsq';
    case 'doubtful':
      return row.linkState === 'doubtful';
    case 'linked':
      return row.linkState === 'linked';
    case 'reserve':
      return row.role === 'reserve';
  }
}

/** A selected row can be replaced once a person found it is not in FSQ. */
export function canReplacePilot(row: PilotRow): boolean {
  return row.role === 'selected' && row.linkState === 'not_in_fsq';
}

/** Reserve rows that can take a selected row's place: same zone, already linked to FSQ. */
export function pilotReserveFor(rows: readonly PilotRow[], selected: PilotRow): PilotRow[] {
  return rows
    .filter(
      (row) =>
        row.role === 'reserve' && row.zoneId === selected.zoneId && row.linkState === 'linked',
    )
    .sort((a, b) => a.listRank - b.listRank);
}
